import React from "react";
import { motion } from "framer-motion";

interface TechSectionProps {
  techImage: string;
  easyUseImage: string;
  laptopImages: string[];
}

export const TechSection: React.FC<TechSectionProps> = ({
  techImage,
  easyUseImage,
  laptopImages,
}) => {
  return (
    <>
      {/* Tech Title Section */}
      <section className="py-16 md:py-32 bg-gradient-to-b from-white to-purple-50 relative overflow-hidden">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div
            className="text-center mb-8 md:mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              혁신적인 차트 시스템
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto px-4">
              비교할 수 없는 속도로 경험하는 가장 혁신적인 비주얼 차트. 진단과
              수납에 필요한 핵심 기능만으로 설계되어, 초보자도 쉽게 사용하고
              한눈에 이해할 수 있습니다. 선도적인 기술 기업들이 사용하는 최첨단
              기술로 구축되었습니다.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tech Image Section */}
      <section
        className="relative bg-gradient-to-b from-purple-50 to-blue-50"
        style={{ padding: 0, margin: "-50px 0 -50px 0", overflow: "hidden" }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ width: "100vw", marginLeft: "calc(-50vw + 50%)" }}
        >
          <img
            src={techImage}
            alt="Tech"
            style={{
              width: "100%",
              height: "auto",
              objectFit: "cover",
              display: "block",
            }}
          />
        </motion.div>
      </section>

      {/* Performance Section */}
      <section className="py-16 md:py-32 bg-gradient-to-b from-purple-50 to-pink-50 relative overflow-visible">
        {/* Animated image carousel in background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5 overflow-hidden">
          {laptopImages.map((img, idx) => (
            <motion.img
              key={idx}
              src={img}
              alt={`bg-${idx}`}
              className="absolute w-64 rounded-xl"
              animate={{
                x: [
                  Math.cos((idx * Math.PI) / 2) * 300,
                  Math.cos((idx * Math.PI) / 2 + Math.PI) * 300,
                  Math.cos((idx * Math.PI) / 2) * 300,
                ],
                y: [
                  Math.sin((idx * Math.PI) / 2) * 200,
                  Math.sin((idx * Math.PI) / 2 + Math.PI) * 200,
                  Math.sin((idx * Math.PI) / 2) * 200,
                ],
                rotate: [0, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                delay: idx * 2,
                ease: "linear",
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.h2
            className="text-3xl md:text-4xl lg:text-6xl font-bold text-center mb-12 md:mb-20 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Active & Interactive Performance
          </motion.h2>

          {/* Easy Use Image */}
          <motion.div
            className="flex justify-center mb-8 md:mb-16"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img
              src={easyUseImage}
              alt="사용하기 쉬운 인터페이스"
              className="max-w-4xl w-full h-auto rounded-2xl md:rounded-3xl shadow-2xl"
            />
          </motion.div>
        </div>
      </section>
    </>
  );
};
